import React, { useEffect, useMemo, useState } from 'react';
import CryptoJS from 'crypto-js';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { Loader } from 'semantic-ui-react';
import LanguageButton from '../../components/nav/Links/LanguageButton';
import { apiV2Server } from '../../config';
import { apiService } from '../../services';
import { isValidRXLT, Parsers } from '../../helpers';
import { renderStatusHistory } from '../../helpers/statusHistory';
import { formatDateToUserLocalStr } from '../../helpers/tools';
import { getLanguage } from '../../helpers/getLocationLanguage';
import GoogleMap from './googleMap';
import * as Sentry from "@sentry/browser";

const DropOffPoint = ({ getPublicMetadataAPI, location, history, match }) => {
  const [isLoading, setIsLoading] = useState();
  const [cityOptionsMap, setCityOptionsMap] = useState();
  const [selectedCity, setSelectedCity] = useState();
  const [selectedDropOffPoint, setSelectedDropOffPoint] = useState();
  const [statusHistory, setStatusHistory] = useState();
  const [showMap, setShowMap] = useState();
  const [isMapNonDisplayable, setisMapNonDisplayable] = useState();
  const [googleMapURL, setGoogleMapURL] = useState();
  const [isChangeable, setIsChangeable] = useState();
  const [isViewOnly, setIsViewOnly] = useState();
  const [error, setError] = useState();
  const [success, setSuccess] = useState();
  const [token, setToken] = useState();
  const [infoMessage, setInfoMessage] = useState();

  const language = useMemo(() => getLanguage(location), [location]);
  const { t } = useTranslation('dropOffPoint');

  function _buildDropOffPointOption(pudo) {
    const { name, address: { stAddress, houseNumber } } = pudo;
    return { label: `${stAddress} ${houseNumber} (${name})`, value: pudo };
  }

  function _buildCityOption(pudo) {
    const { address: { city } } = pudo;
    return { label: city, value: pudo, dropOffPointOptions: [_buildDropOffPointOption(pudo)] };
  }

  function createCityOptionsMap(data) {
    if (!data.length) {
      return;
    }

    const m = data.reduce((mp, pudo) => {
      const { address: { city } } = pudo;
      mp[city]
        ? mp[city].dropOffPointOptions.push(_buildDropOffPointOption(pudo))
        : mp[city] = _buildCityOption(pudo);

      return mp;
    }, {});

    setCityOptionsMap(m);

    if (selectedCity) {
      const { label } = selectedCity;
      const cityInfo = m[label];
      if (!cityInfo) {
        console.error(`Unexpected selectedCity ${label}`);
        return;
      }
      selectedCity.dropOffPointOptions = cityInfo.dropOffPointOptions;
      setSelectedCity(selectedCity);
    }
  }

  function resetAllMessages() {
    setSuccess();
    setError();
    setInfoMessage();
  }

  function setSelectedPudo(pudo) {
    if (!pudo) {
      console.error('Cannot set pudo: no pudo found');
      return;
    }

    setSelectedCity(_buildCityOption(pudo));
    setSelectedDropOffPoint(_buildDropOffPointOption(pudo));
  }

  function parseAndSetStatusHistory(statuses) {
    if (!statuses) {
      console.error('Cannot set statusHistory: no statusHistory found');
      return;
    }
    statuses.forEach(val => {
      const { date } = val;
      val.date = date ? formatDateToUserLocalStr(date) : null;
    });
    setStatusHistory(statuses);
  }

  function fetchListOfPudo() {
    const foundRxltID = Parsers.sanitizeString(match.params.rxltID);
    if (!isValidRXLT(foundRxltID)) {
      setError('errorRXLT');
      return;
    }

    return fetch(`${apiV2Server}returns/pudo/${foundRxltID}`)
      .then((res) => res.json().then((data) => {
        if (data) {
          createCityOptionsMap(data.dropOff);
        }
      }))
      .catch((err) => {
        console.error('Error fetching PUDO list', err);
        Sentry.captureException(err);
      });
  }

  function getGoogleMapUrl() {
    return apiService
      .callServer(
        getPublicMetadataAPI, null, null, null, null, true
      )
      .then(({ google_maps_url: url }) => {
        if (!url) {
          console.error('Unexpected response from metadata call');
          return;
        }

        setGoogleMapURL(CryptoJS.AES.decrypt(url, 'exelot_pass').toString(CryptoJS.enc.Utf8));
      });
  }

  useEffect(() => {
    const search = new URLSearchParams(location ? (location.search || '') : '');

    const tokenVal = search.get('token');
    setToken(tokenVal);

    const foundRxltID = Parsers.sanitizeString(match.params.rxltID);
    if (!isValidRXLT(foundRxltID)) {
      setError('errorRXLT');
      return;
    }

    setIsLoading(true);
    fetch(`${apiV2Server}returns/return/${foundRxltID}/pudo/status${tokenVal ? `?token=${tokenVal}` : ''}`)
      .then((res) => {
        const { status } = res;
        if (status === 404 || status === 401) {
          setError('errorAddressInvalid');
          return;
        }
        if (status !== 200) {
          setError('unexpectedError');
          return;
        }
        return res.json().then(({ pudoStatus, pudoInfo, pudoStatusHistory, returnsCreationType }) => {
          parseAndSetStatusHistory(pudoStatusHistory);
          switch (pudoStatus) {
            case 'pudoSetUnchangeable':
              setError('pudoSetUnchangeableError');
              setSelectedPudo(pudoInfo);
              break;
            case 'pudoInvalidAddress':
              setError('errorNoToken');
              setSelectedPudo(pudoInfo);
              break;
            case 'pudoChangeable':
              setSelectedPudo(pudoInfo);
              setIsChangeable(true);
              return fetchListOfPudo();
            case 'pudoNotSet':
              switch (returnsCreationType) {
                case 'orderCreation':
                  setIsViewOnly(true);
                  break;
                default:
                  setInfoMessage('pudoNotSelected');
                  setIsChangeable(true);
                  break;
              }
              return fetchListOfPudo();
            default: {
              throw new Error(`Unexpected pudoStatus ${pudoStatus}`);
            }
          }
        });
      })
      .then(() => {
        return getGoogleMapUrl()
          .catch(err => {
            console.error('Error on request to get google maps url', err);
          });
      })
      .then(() => setIsLoading())
      .catch((err) => {
        console.error(err);
        setError('errorOnGetList');
        setIsLoading();
        Sentry.captureException(err);
      });
  }, []);

  function onSubmit() {
    if (!isChangeable || isViewOnly) {
      // Should never happen
      return;
    }

    const foundRxltID = Parsers.sanitizeString(match.params.rxltID);
    if (!isValidRXLT(foundRxltID)) {
      setError('errorRXLT');
      return;
    }
    if (!token) {
      setError('errorNoToken');
      return;
    }

    setIsLoading(true);
    resetAllMessages();

    fetch(`${apiV2Server}returns/return/${foundRxltID}/pudo?token=${token}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ PUDO: selectedDropOffPoint.value.code })
    })
      .then((res) => {
        setIsLoading();
        const { status } = res;
        if (status === 401) {
          setError(`${t('errorOnSubmit')} ${res.statusText}`);
          return;
        }

        if (status === 404) {
          setError('errorOnSubmitNotFound');
          return;
        }

        if (status !== 200) {
          setError('errorOnSubmit');
          return;
        }

        setSuccess('successOnSubmit');
        setIsChangeable();
        setIsViewOnly();
      })
      .catch((err) => {
        console.error(err);
        setError('errorOnSubmit');
        setIsLoading();
      });
  }

  function handleChangeCity(option) {
    const dropOffPointOptions = option ? option.dropOffPointOptions : null;

    setSelectedCity(option);

    setSelectedDropOffPoint(
      dropOffPointOptions && dropOffPointOptions.length === 1
        ? dropOffPointOptions[0]
        : null
    );

    resetAllMessages();
  }

  function handleChangeDropOffPoint(option) {
    setSelectedDropOffPoint(option);
    resetAllMessages();
  }

  function handleChangeDropOffPointAndCity(option) {
    let selectedPoint = option;
    const { dropOffPointOptions } = option;
    if (dropOffPointOptions && dropOffPointOptions.length) {
      const pointCode = option.value.code;
      selectedPoint = dropOffPointOptions.find(x => x.value.code === pointCode);
      setSelectedCity(option);
    }
    setSelectedDropOffPoint(selectedPoint);
    resetAllMessages();
  }

  const canShowMapButton = googleMapURL && (selectedCity || cityOptionsMap);

  const dropOffPointOptions = selectedCity ? selectedCity.dropOffPointOptions : null;
  const cityOptions = cityOptionsMap ? Object.values(cityOptionsMap) : null;

  return (
    <>
      <div style={{ marginTop: '20px' }}>
        {isLoading && (
          <Loader size='huge' active />
        )}

        <LanguageButton location={location} history={history} language={language} token={token} />
      </div>

      <div className={`drop-off-point ${language}`}>

        <div style={{ marginBottom: '35px' }}>
          <h1 className='bold text-center'>
            {t('title')}
          </h1>

          {(isChangeable || isViewOnly) && (
            <>
              <p className='subtitle text-center'>{t('subtitle')}</p>
              <div className={`selections-wrap ${language}`}>
                <Select
                  className='basic-single'
                  isDisabled={!cityOptions}
                  isClearable
                  isSearchable
                  name='city'
                  options={cityOptions || []}
                  placeholder={t('placeholderCity')}
                  value={selectedCity}
                  onChange={handleChangeCity}
                />
                <Select
                  className='basic-single'
                  isDisabled={!selectedCity}
                  isClearable
                  isSearchable
                  name='drop-off-point'
                  options={dropOffPointOptions || []}
                  placeholder={t('placeholderDropOffPoint')}
                  value={selectedDropOffPoint}
                  onChange={handleChangeDropOffPoint}
                />
              </div>
            </>
          )}
          {( canShowMapButton && !isMapNonDisplayable && showMap &&
            <div style={{ marginBottom: '20px' }}>
              <GoogleMap
                googleMapURL={googleMapURL}
                opts={{
                  boundOpts: selectedDropOffPoint
                    ? [selectedDropOffPoint]
                    : dropOffPointOptions,
                  markerOpts: dropOffPointOptions || cityOptions
                }}
                // selection={selectedDropOffPoint}
                onMarkerClick={handleChangeDropOffPointAndCity}
                onError={() => {
                  setisMapNonDisplayable(true);
                }}
              />
            </div>
          )}
          <div className="text-center">
              <button
                style={{ marginLeft: '10%' }}
                className={`btn ${showMap ? 'white' : 'orange'}`}
                onClick={() => setShowMap(!showMap)}
              >
                {showMap ? t('hideMap') : t('showMap')}
              </button>
          </div>

          {selectedDropOffPoint && (
            <div className='selected-drop-off-point'>
              <div className='title'>
                {t('additionalInformationTitle')}
              </div>
              <p className='additional-info'>{t('city')} <span>{selectedDropOffPoint.value.address.city}</span></p>
              <p className='additional-info'>{t('name')} <span>{selectedDropOffPoint.value.name}</span></p>
              <p className='additional-info'>{t('additionalInformation')} <span>{selectedDropOffPoint.value.additionalInformation}</span></p>

              {isChangeable && !isViewOnly && (
                <div className='button-wrap'>
                  <button className='btn small orange' onClick={onSubmit} disabled={isLoading}>{t('submit')}</button>
                </div>
              )}
            </div>
          )}

          {infoMessage &&
            <div className='animated bounceIn status-payment-box info'>
              <div><i className='glyphicon glyphicon-exclamation-sign' aria-hidden='true' /></div>
              {t(infoMessage)}
            </div>}

          {error &&
            <div className='animated bounceIn status-payment-box error'>
              <div><i className='glyphicon glyphicon-remove-circle' aria-hidden='true' /></div>
              {t(error)}
            </div>}

          {success &&
            <div className='animated bounceIn status-payment-box success'>
              <div><i className='glyphicon glyphicon-ok-circle' aria-hidden='true' /></div>
              {t(success)}
            </div>}
        </div>

        {statusHistory ? renderStatusHistory(statusHistory, { title: t('dropOffHistory'), language }) : null}
      </div>
    </>
  );
};

function mapStateToProps(state) {
  const { getPublicMetadata } = state.api;

  return {
    getPublicMetadataAPI: getPublicMetadata
  };
}

export default connect(mapStateToProps)(DropOffPoint);
